/* eslint-disable max-lines */
import { gql } from 'graphql-tag';

import { CountryFragments } from './CountryType';
import { CountyFragments } from './CountyType';
import { ImageFragments } from './ImageType';
import { RegionFragments } from './RegionType';
import { SpeciesFragments } from './SpeciesTypes';

const fishingWaterId = gql`
  fragment FishingWaterId on FishingWater {
    _id: externalId
  }
`;

const fishingWaterLocationFields = gql`
  fragment FishingWaterLocationFields on FishingWater {
    latitude
    longitude
    country {
      ...CountryId
      countryName
    }
    region {
      ...FishingWaterRegionFields
    }
  }
  ${CountryFragments.id}
  ${RegionFragments.fishingWaterRegionFields}
`;

const fishingWaterLocationFieldsExtended = gql`
  fragment FishingWaterLocationFieldsExtended on FishingWater {
    latitude
    longitude
    county {
      ...FishingWaterCountyFields
    }
    intersectingCounties(first: 12) {
      edges {
        node {
          ...IntersectingCountyNodeFields
        }
      }
    }
    country {
      ...FishingWaterCountryFields
    }
    region {
      ...FishingWaterRegionFields
    }
  }
  ${CountryFragments.fishingWaterCountryFields}
  ${CountyFragments.fishingWaterCountyFields}
  ${CountyFragments.intersectingCountyNodeFields}
  ${RegionFragments.fishingWaterRegionFields}
`;

const fishingWaterSummaryMetadataFields = gql`
  fragment FishingWaterSummaryMetadataFields on FishingWaterMetadata {
    id
    description
    name
    variant
    group {
      id
    }
  }
`;

const fishingWaterDetailMetadataFields = gql`
  fragment FishingWaterDetailMetadataFields on FishingWaterMetadata {
    id
    description
    group {
      id
    }
    name
    variant
  }
`;

const placeTopWaterNodeFields = gql`
  fragment PlaceTopWaterNodeFields on FishingWater {
    ...FishingWaterId
    ...FishingWaterLocationFields
    catchesCount
    displayName
    allReviews: reviews {
      totalCount
      totalAverage(attribute: RATING)
    }
  }
  ${fishingWaterId}
  ${fishingWaterLocationFields}
`;

/**
 * Note: CountryTopWaterNodeFields depends on CatchFragments.countryTopWaterRecentCatchNodeFields
 * (CountryTopWaterRecentCatchNodeFields), PostFragments.id (PostId), SpeciesFragments.id (SpeciesId),
 * UserFragments.id (UserId), CatchFragments.id (CatchId), and ImageFragments.imageFields (ImageFields).
 * To avoid circular imports (CatchType imports FishingWaterType), those dependencies are NOT included
 * here. The query that uses this fragment must include them.
 */
const countryTopWaterNodeFields = gql`
  fragment CountryTopWaterNodeFields on FishingWater {
    ...FishingWaterId
    ...FishingWaterLocationFields
    catchesCount
    displayName
    allReviews: reviews {
      totalCount
      totalAverage(attribute: RATING)
    }
    recentCatches: catches(
      first: 4
      filters: { withPhotos: true, orderByRecent: true, speciesIds: $speciesIds }
    ) {
      edges {
        node {
          ...CountryTopWaterRecentCatchNodeFields
        }
      }
    }
  }
  ${fishingWaterId}
  ${fishingWaterLocationFields}
`;

/**
 * Note: RegionTopWaterNodeFields depends on CatchFragments.id (CatchId),
 * PostFragments.id (PostId), and ImageFragments.imageFields (ImageFields).
 * To avoid circular imports (CatchType imports FishingWaterType), those dependencies
 * are NOT included here. The query that uses this fragment must include them.
 */
const regionTopWaterNodeFields = gql`
  fragment RegionTopWaterNodeFields on FishingWater {
    ...FishingWaterId
    ...FishingWaterLocationFields
    catchesCount
    displayName
    descriptionOfWaterType
    allReviews: reviews {
      totalCount
      totalAverage(attribute: RATING)
    }
    recentCatches: catches(
      first: 4
      filters: { withPhotos: true, orderByRecent: true, speciesIds: $speciesIds }
    ) {
      edges {
        node {
          ...CatchId
          post {
            ...PostId
            images(first: 1, croppingStrategy: SMART, height: 400, width: 400) {
              totalCount
              edges {
                node {
                  ...ImageFields
                }
              }
            }
          }
        }
      }
    }
  }
  ${fishingWaterId}
  ${fishingWaterLocationFields}
`;

/**
 * PlaceTopWaterWithCatchesNodeFields extends PlaceTopWaterNodeFields with recentCatches.
 * Depends on CatchId, PostId, ImageFields (must be included by consuming query).
 */
const placeTopWaterWithCatchesNodeFields = gql`
  fragment PlaceTopWaterWithCatchesNodeFields on FishingWater {
    ...PlaceTopWaterNodeFields
    recentCatches: catches(first: 4, filters: { withPhotos: true, orderByRecent: true }) {
      edges {
        node {
          ...CatchId
          post {
            ...PostId
            images(first: 1, croppingStrategy: SMART, height: 400, width: 400) {
              totalCount
              edges {
                node {
                  ...ImageFields
                }
              }
            }
          }
        }
      }
    }
  }
  ${placeTopWaterNodeFields}
`;

export const FishingWaterFragments = {
  id: fishingWaterId,
  locationFields: fishingWaterLocationFields,
  locationFieldsExtended: fishingWaterLocationFieldsExtended,
  countryTopWaterNodeFields,
  regionTopWaterNodeFields,
  placeTopWaterNodeFields,
  placeTopWaterWithCatchesNodeFields,
  watersListItem: gql`
    fragment FishingWaterWatersListItem on FishingWater {
      _id: externalId
      latitude
      longitude
      displayName
      catchesCount
      followedByCurrentUser
      catches(last: 1, filters: { withPhotos: true }) {
        edges {
          node {
            _id: externalId
            post {
              _id: externalId
              images(first: 1, croppingStrategy: SMART, height: 100, width: 100) {
                edges {
                  node {
                    id
                    ...ImageFields
                  }
                }
              }
            }
          }
        }
      }
      reviews {
        totalCount
        totalAverage(attribute: RATING)
      }
      topSpecies(first: 3) {
        edges {
          node {
            ...SpeciesId
            displayName
          }
        }
      }
    }
    ${ImageFragments.imageFields}
    ${SpeciesFragments.id}
  `,
  carouselItem: gql`
    fragment FishingWaterCarouselItem on FishingWater {
      _id: externalId
      latitude
      longitude
      displayName
      reviews {
        totalCount
        totalAverage(attribute: RATING)
      }
      country {
        ...CountryId
        countryName
      }
      region {
        ...RegionId
        name
        localName
      }
      catchesCount
    }
    ${CountryFragments.id}
    ${RegionFragments.id}
  `,
  summaryMetadataFields: fishingWaterSummaryMetadataFields,
  /**
   * Note: FishingWaterSummaryFields depends on CatchFieldsForPostsViewer.
   * To avoid circular imports, it is NOT included here. The query that uses
   * this fragment must include CatchFieldsForPostsViewer.
   */
  summaryFields: gql`
    fragment FishingWaterSummaryFields on FishingWater {
      ...FishingWaterId
      ...FishingWaterLocationFields
      displayName
      metadata {
        ...FishingWaterSummaryMetadataFields
      }
      catchesCount
      allReviews: reviews {
        totalCount
        totalAverage(attribute: RATING)
      }
      catches(first: $first, after: $after, filters: { withPhotos: true, orderByRecent: true }) {
        totalCount
        pageInfo {
          startCursor
          hasNextPage
          endCursor
        }
        edges {
          node {
            ...CatchFieldsForPostsViewer
          }
        }
      }
    }
    ${fishingWaterId}
    ${fishingWaterLocationFields}
    ${fishingWaterSummaryMetadataFields}
  `,
  suggestedWaterFields: gql`
    fragment SuggestedWaterFields on FishingWater {
      ...FishingWaterId
      ...FishingWaterLocationFields
      displayName
      catchesCount
      newCatchCount: catches(filters: { lastNDays: $recentCatchLimitDays }) {
        totalCount
      }
      topSpeciesSummary(first: 3) {
        edges {
          node {
            displayName
          }
        }
      }
      metadata {
        id
        description
        group {
          id
        }
        name
        variant
      }
    }
    ${fishingWaterId}
    ${fishingWaterLocationFields}
  `,
  detailMetadataFields: fishingWaterDetailMetadataFields,
  /**
   * Note: FishingWaterDetailFields depends on fragments from CatchType, UserType,
   * and FishingWaterReviewType. To avoid circular imports, those dependencies are
   * NOT included here. The query that uses this fragment must include them:
   * - CatchFragments.fishingWaterDetailCatchNodeFields
   * - UserFragments.fishingWaterDetailTopAnglersFields
   * - FishingWaterReviewFragments.allReviewsFields
   * - FishingWaterReviewFragments.amenityNodeFields
   */
  detailFields: gql`
    fragment FishingWaterDetailFields on FishingWater {
      ...FishingWaterId
      ...FishingWaterLocationFieldsExtended
      displayName
      metadata {
        ...FishingWaterDetailMetadataFields
      }
      catchesCount
      topAnglers: catches(filters: { withPhotos: true }) {
        anglers: topAnglers(first: 3) {
          ...FishingWaterDetailTopAnglersFields
        }
      }
      catches(first: 6, filters: { withPhotos: true, orderByCaughtAt: true }) {
        edges {
          node {
            ...FishingWaterDetailCatchNodeFields
          }
        }
      }
      descriptionOfWaterType
      allReviews: reviews {
        ...FishingWaterDetailAllReviewsFields
      }
      reviews(first: 6, filters: { havingTextOrTitle: true, orderByHelpfulVotes: true }) {
        amenities: propertyQuestions(filters: { highlighted: true }, first: 20) {
          __typename
          edges {
            __typename
            node {
              ...FishingWaterDetailAmenityNodeFields
            }
          }
        }
      }
      url
    }
    ${fishingWaterId}
    ${fishingWaterLocationFieldsExtended}
    ${fishingWaterDetailMetadataFields}
  `,
  metadataNodeFields: gql`
    fragment FishingWaterMetadataNodeFields on FishingWaterMetadata {
      __typename
      id
      name
      group {
        id
        name
        choice
      }
    }
  `,
  topWatersInfoNodeFields: gql`
    fragment TopWatersInfoNodeFields on FishingWater {
      externalId
      catchesCount
      displayName
      descriptionOfWaterType
      region {
        ...RegionId
        name
      }
    }
    ${RegionFragments.id}
  `,
  watersInRegionBySpeciesNodeFields: gql`
    fragment WatersInRegionBySpeciesNodeFields on FishingWater {
      ...FishingWaterId
      displayName
      catchesCount
    }
    ${fishingWaterId}
  `,
};
